import React, { useState } from "react";
import PropTypes from "prop-types";
import { Button, Checkbox, Modal } from "@opendate-app/setlist";
import client from "../../../shared/api/client";
import { toastError, toastSuccess } from "../../../shared/toastHelper";

const rowStyle = { display: "flex", gap: 12, alignItems: "flex-start", padding: "12px 0", borderBottom: "1px solid var(--sl-border-secondary)" };
const smallText = { fontSize: 12, lineHeight: "18px", color: "var(--sl-text-quaternary)" };
const titleText = { fontSize: 14, lineHeight: "20px", fontWeight: 600, color: "var(--sl-text-primary)" };
const detailText = { fontSize: 13, lineHeight: "18px", color: "var(--sl-text-tertiary)" };

const Section = ({ title, children }) => (
  <div>
    <div style={{ ...smallText, fontWeight: 600, padding: "12px 0 4px", textTransform: "uppercase", letterSpacing: "0.04em" }}>{title}</div>
    {children}
  </div>
);

Section.propTypes = { title: PropTypes.string.isRequired, children: PropTypes.node };

const shiftLabel = (shift) => [shift.day, shift.times, shift.event].filter(Boolean).join(" · ");

const toggled = (event) => (event?.target ? event.target.checked : event);

const SmartScheduleModal = ({ preview, applyPath, venueOwnershipId, week, onClose, onApplied }) => {
  const [skipped, setSkipped] = useState(() => new Set());
  const [saving, setSaving] = useState(false);

  const setKept = (key, kept) =>
    setSkipped((current) => {
      const next = new Set(current);
      if (kept) next.delete(key);
      else next.add(key);
      return next;
    });

  const createdKeys = new Set(preview.creates.filter((item) => !skipped.has(`event:${item.event_id}`)).flatMap((item) => item.keys));
  const pendingKeys = new Set(preview.creates.flatMap((item) => item.keys));
  const assignments = preview.assignments.filter((line) => !skipped.has(line.key) && (!pendingKeys.has(line.key) || createdKeys.has(line.key)));
  const changes = createdKeys.size + assignments.length;

  const apply = async () => {
    if (!changes || saving) return;
    setSaving(true);
    let result;
    try {
      const { data } = await client.post(applyPath, {
        venue_ownership_id: venueOwnershipId,
        week,
        create_keys: Array.from(createdKeys),
        assignments: assignments.map((line) => ({ key: line.key, staff_member_id: line.staff_member_id })),
      });
      result = data;
    } catch (error) {
      setSaving(false);
      toastError(error?.response?.data?.errors?.[0] || "Could not apply Smart Schedule.");
      return;
    }
    const done = [result.created && `created ${result.created} ${result.created === 1 ? "shift" : "shifts"}`, result.assigned && `assigned ${result.assigned}`].filter(Boolean).join(", ");
    const stale = result.skipped ? ` ${result.skipped} changed since the preview and ${result.skipped === 1 ? "was" : "were"} left as ${result.skipped === 1 ? "it is" : "they are"}.` : "";
    toastSuccess(done ? `${done[0].toUpperCase()}${done.slice(1)}. Saved as drafts; nobody is notified until you publish.${stale}` : `Nothing changed.${stale}`);
    onApplied();
  };

  const creates = preview.creates.filter((item) => item.keys.length > 0);
  const skippedShows = preview.creates.filter((item) => item.keys.length === 0);
  const nothing = !preview.creates.length && !preview.assignments.length && !preview.open.length;

  return (
    <Modal
      open
      width={640}
      showClose={false}
      onClose={onClose}
      title="Smart Schedule"
      description="Fills this week's open draft shifts with the people who usually work them and are free. Untick anything you don't want. Nothing is published."
      footer={
        <div className="d-flex justify-content-end" style={{ gap: 12, width: "100%" }}>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={apply} disabled={saving || !changes}>
            {`Apply ${changes} ${changes === 1 ? "change" : "changes"}`}
          </Button>
        </div>
      }
    >
      <div style={{ borderTop: "1px solid var(--sl-border-secondary)", maxHeight: "60vh", overflowY: "auto" }}>
        {nothing && <div style={{ ...detailText, padding: "16px 0" }}>No open draft shifts this week, and no shows waiting on a template.</div>}
        {creates.length > 0 && (
          <Section title="Shifts to create">
            {creates.map((item) => (
              <div key={item.event_id} className="smart-schedule-create" style={rowStyle}>
                <Checkbox
                  id={`smart-create-${item.event_id}`}
                  checked={!skipped.has(`event:${item.event_id}`)}
                  onChange={(event) => setKept(`event:${item.event_id}`, toggled(event))}
                  aria-label={`Create shifts for ${item.title}`}
                />
                <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                  <span style={smallText}>{`${item.date_label} · ${item.room_label}`}</span>
                  <span style={titleText}>{item.title}</span>
                  <span style={detailText}>{item.detail}</span>
                </div>
              </div>
            ))}
          </Section>
        )}
        {skippedShows.length > 0 && (
          <Section title="Skipped shows">
            {skippedShows.map((item) => (
              <div key={item.event_id} className="smart-schedule-skipped-show" style={{ ...rowStyle, opacity: 0.6 }}>
                <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                  <span style={smallText}>{`${item.date_label} · ${item.room_label}`}</span>
                  <span style={titleText}>{item.title}</span>
                  <span style={detailText}>{item.detail}</span>
                </div>
              </div>
            ))}
          </Section>
        )}
        {preview.assignments.length > 0 && (
          <Section title="Proposed assignments">
            {preview.assignments.map((line) => {
              const unavailable = pendingKeys.has(line.key) && !createdKeys.has(line.key);
              return (
                <div key={line.key} className="smart-schedule-assignment" style={{ ...rowStyle, opacity: unavailable ? 0.5 : 1 }}>
                  <Checkbox
                    id={`smart-assign-${line.key}`}
                    checked={!unavailable && !skipped.has(line.key)}
                    disabled={unavailable}
                    onChange={(event) => setKept(line.key, toggled(event))}
                    aria-label={`Assign ${line.name}`}
                  />
                  <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                    <span style={smallText}>{shiftLabel(line.shift)}</span>
                    <span style={titleText}>{`${line.shift.position} → ${line.name}`}</span>
                    <span style={detailText}>{line.reason}</span>
                  </div>
                </div>
              );
            })}
          </Section>
        )}
        {preview.open.length > 0 && (
          <Section title="Left open">
            {preview.open.map((line) => (
              <div key={line.key} className="smart-schedule-open" style={rowStyle}>
                <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                  <span style={smallText}>{shiftLabel(line.shift)}</span>
                  <span style={titleText}>{line.shift.position}</span>
                  <span style={{ ...detailText, color: "var(--sl-text-warning)" }}>{line.reason}</span>
                </div>
              </div>
            ))}
          </Section>
        )}
      </div>
    </Modal>
  );
};

const shiftShape = PropTypes.shape({
  day: PropTypes.string.isRequired,
  times: PropTypes.string.isRequired,
  position: PropTypes.string.isRequired,
  event: PropTypes.string,
});

SmartScheduleModal.propTypes = {
  preview: PropTypes.shape({
    creates: PropTypes.arrayOf(
      PropTypes.shape({
        event_id: PropTypes.number.isRequired,
        title: PropTypes.string.isRequired,
        date_label: PropTypes.string.isRequired,
        room_label: PropTypes.string.isRequired,
        detail: PropTypes.string.isRequired,
        keys: PropTypes.arrayOf(PropTypes.string).isRequired,
      }),
    ).isRequired,
    assignments: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        staff_member_id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        reason: PropTypes.string.isRequired,
        shift: shiftShape.isRequired,
      }),
    ).isRequired,
    open: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        reason: PropTypes.string.isRequired,
        shift: shiftShape.isRequired,
      }),
    ).isRequired,
  }).isRequired,
  applyPath: PropTypes.string.isRequired,
  venueOwnershipId: PropTypes.number.isRequired,
  week: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onApplied: PropTypes.func.isRequired,
};

export default SmartScheduleModal;
